import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, Sparkles } from 'lucide-react';
import { api } from '../../app/api';
import { logger } from '../../lib/logger';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { BlogShell } from './BlogShell';
import { useDocumentHead } from './useDocumentHead';

const SITE_BASE = 'https://apexathleticmind.com';
const DEFAULT_HERO = '/blog/heroes/5-steps-performance-anxiety-og.jpg';

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function BlogIndex() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/api/blog/posts')
      .then((data) => { if (active) setPosts(data?.posts || []); })
      .catch((err) => {
        logger.error('Failed to load blog index', err);
        if (active) setError(err.message || 'Could not load posts');
      });
    return () => { active = false; };
  }, []);

  useDocumentHead({
    title: 'The Apex Journal — Mental Performance Blog | Apex Athletic Mind',
    description: 'Sports psychology essays for serious athletes, parents, and coaches. Mental performance frameworks from Coach Varsay Lewis, M.Ed.',
    canonical: `${SITE_BASE}/blog`,
    ogType: 'website',
    ogImage: `${SITE_BASE}${DEFAULT_HERO}`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'The Apex Journal',
      url: `${SITE_BASE}/blog`,
      description: 'Mental performance training insights for serious athletes, parents, and coaches.',
      publisher: {
        '@type': 'Organization',
        name: 'Apex Athletic Mind Consulting LLC',
        url: SITE_BASE,
      },
    },
  });

  const featured = posts && posts.length > 0 ? posts[0] : null;
  const rest = posts && posts.length > 1 ? posts.slice(1) : [];

  return (
    <BlogShell testId="blog-index-page">
      {/* Hero / intro */}
      <section className="max-w-[1120px] mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8">
        <div className="flex items-center gap-2 font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#004C54] font-semibold mb-3">
          <Sparkles className="h-3.5 w-3.5" />
          The Apex Journal
        </div>
        <h1
          className="font-heading text-[2.4rem] sm:text-[3rem] font-bold tracking-tight leading-[1.05] text-[#1a1a1a] mb-4 max-w-[18ch]"
          data-testid="blog-index-title"
        >
          Mental performance, written for athletes.
        </h1>
        <p className="text-[16px] sm:text-[17px] text-[#3a3a3a] leading-[1.7] max-w-[58ch]">
          Essays, frameworks, and game-day playbooks from Coach Varsay Lewis, M.Ed. — built to train the skill that decides every championship: <em>your ability to perform under pressure.</em>
        </p>
      </section>

      {/* Posts */}
      <section className="max-w-[1120px] mx-auto px-5 sm:px-8 pb-16">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-[14px] text-red-700" role="alert" data-testid="blog-index-error">
            {error}
          </div>
        )}

        {!posts && !error && (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-index-loading">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={`sk-${i}`} className="space-y-3">
                <Skeleton className="aspect-[16/10] rounded-2xl" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {posts && posts.length === 0 && (
          <div
            className="rounded-3xl border border-black/10 bg-white p-10 sm:p-14 text-center shadow-sm"
            data-testid="blog-index-empty"
          >
            <div className="mx-auto h-14 w-14 rounded-2xl bg-[#004C54]/8 flex items-center justify-center mb-4">
              <Sparkles className="h-6 w-6 text-[#004C54]" />
            </div>
            <h2 className="font-heading text-[1.4rem] font-semibold mb-2">Fresh essays coming soon.</h2>
            <p className="text-[15px] text-[#5a5a5a] max-w-[44ch] mx-auto mb-6">
              We're publishing our first round of sports-psychology essays. While you wait, take the free mindset quiz or grab the playbook.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 px-5 rounded-xl" data-testid="blog-empty-quiz-cta">
                <Link to="/quiz">Take the Mindset Quiz</Link>
              </Button>
              <Button asChild variant="outline" className="h-11 px-5 rounded-xl border-[#004C54]/30 text-[#004C54] hover:bg-[#004C54]/5" data-testid="blog-empty-playbook-cta">
                <Link to="/playbook">Get the Free Playbook</Link>
              </Button>
            </div>
          </div>
        )}

        {featured && (
          <Link
            to={`/blog/${featured.slug}`}
            className="group block mb-12 rounded-3xl overflow-hidden border border-black/8 bg-white shadow-sm hover:shadow-xl transition-shadow"
            data-testid="blog-featured-card"
          >
            <div className="grid md:grid-cols-[1.4fr_1fr]">
              <div className="relative aspect-[16/10] md:aspect-auto overflow-hidden bg-[#0a0d0e]">
                <img
                  src={featured.hero_image_url || DEFAULT_HERO}
                  alt={featured.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="eager"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/95 text-[#004C54] hover:bg-white border-0 font-mono-accent uppercase tracking-[0.2em] text-[9px] font-semibold px-2.5 py-1">
                    Featured
                  </Badge>
                </div>
              </div>
              <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-center">
                {featured.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {featured.tags.slice(0, 3).map((t) => (
                      <span key={t} className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-[#004C54] font-semibold">#{t}</span>
                    ))}
                  </div>
                )}
                <h2 className="font-heading text-[1.6rem] sm:text-[1.85rem] font-bold tracking-tight leading-[1.15] text-[#1a1a1a] mb-3 group-hover:text-[#004C54] transition-colors">
                  {featured.title}
                </h2>
                {(featured.subtitle || featured.excerpt) && (
                  <p className="text-[15px] text-[#3a3a3a] leading-[1.65] max-w-[52ch] mb-5">
                    {featured.subtitle || featured.excerpt}
                  </p>
                )}
                <div className="flex items-center gap-4 text-[12.5px] text-[#6b6b6b]">
                  <span>{formatDate(featured.published_at)}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />~5 min read</span>
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#004C54] group-hover:gap-2.5 transition-all">
                  Read essay
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-post-grid">
            {rest.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-black/8 shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`blog-post-card-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#0a0d0e]">
                  <img
                    src={p.hero_image_url || DEFAULT_HERO}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 sm:p-6 flex-1 flex flex-col">
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {p.tags.slice(0, 3).map((t) => (
                        <span key={t} className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-[#004C54] font-semibold">#{t}</span>
                      ))}
                    </div>
                  )}
                  <h3 className="font-heading text-[1.15rem] font-semibold tracking-tight leading-[1.25] text-[#1a1a1a] mb-2 group-hover:text-[#004C54] transition-colors">
                    {p.title}
                  </h3>
                  {(p.subtitle || p.excerpt) && (
                    <p className="text-[14px] text-[#5a5a5a] leading-[1.6] mb-4 line-clamp-3">
                      {p.subtitle || p.excerpt}
                    </p>
                  )}
                  <div className="mt-auto flex items-center justify-between text-[12px] text-[#7a7a7a]">
                    <span>{formatDate(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1 text-[#004C54] font-semibold group-hover:gap-1.5 transition-all">
                      Read <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </BlogShell>
  );
}
